import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

export type TypeSalesProduct = 'midterm' | 'long_term';
export type UnitClassification =
  | 'GENERAL'
  | 'MIDTERM'
  | 'STAY'
  | 'STAFF'
  | 'SHOWROOM'
  | 'SHORT_TERM'
  | 'FEMALE_ONLY'
  | 'MALE_ONLY';
export interface TypeSalesUnitScope {
  unit_id: string;
  unit_version: number;
  classification: UnitClassification | null;
  content_revision: number;
  content_fingerprint: string | null;
}
export interface TypeSalesCurrentSale {
  revision: number;
  product: TypeSalesProduct;
  channel: 'own_website' | 'external_platform';
  first_move_in_date: string | null;
  pending_outcome: 'pending' | 'unknown' | null;
  midterm_reentry_blocked: boolean;
}
export interface TypeSalesRoomScope {
  room_id: string;
  room_version: number;
  topology_revision: number;
  sale: TypeSalesCurrentSale | null;
  units: TypeSalesUnitScope[];
}
export interface TypeSalesScope {
  property_id: string;
  room_type_id: string;
  property_version: number;
  type_version: number;
  rooms: TypeSalesRoomScope[];
}
export interface TypeSalesContext {
  scope: TypeSalesScope;
  expected_scope_fingerprint: string;
  target_product: TypeSalesProduct;
  first_move_in_date: string | null;
  allowed: boolean;
  blocked_targets: { room_id: string | null; reason_codes: string[] }[];
  proposed_changes: {
    room_id: string;
    changed_unit_ids: string[];
    sale_effect: 'INITIAL_SETUP' | 'SALE_UPDATE';
  }[];
  existing_bookings_unchanged: true;
}
export interface TypeSalesRequest {
  command_id: string;
  target_product: TypeSalesProduct;
  channel: 'own_website';
  first_move_in_date: string | null;
  reverse_confirmed: boolean;
  reason: string;
  expected_scope: TypeSalesScope;
  expected_scope_fingerprint: string;
}
export interface TypeSalesResult {
  target_kind: 'ROOM_TYPE';
  target_id: string;
  result_revision: number;
  before_scope: TypeSalesScope;
  after_scope: TypeSalesScope;
  room_receipts: { room_id: string; command_id: string; result_sale_revision: number }[];
  http_status: 200;
}

const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '객실 유형 판매 정보를 확인하지 못했습니다.',
  });
const record = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);
const exact = (v: unknown, keys: string[]): v is Record<string, unknown> =>
  record(v) && Object.keys(v).length === keys.length && keys.every((k) => k in v);
const positive = (v: unknown) => Number.isSafeInteger(v) && Number(v) > 0;
const nonnegative = (v: unknown) => Number.isSafeInteger(v) && Number(v) >= 0;
const date = (v: unknown) =>
  typeof v === 'string' &&
  /^\d{4}-\d{2}-\d{2}$/.test(v) &&
  !Number.isNaN(Date.parse(`${v}T00:00:00Z`));
const product = (v: unknown): v is TypeSalesProduct => v === 'midterm' || v === 'long_term';
const fingerprint = (v: unknown) => typeof v === 'string' && /^[0-9a-f]{64}$/.test(v);
const envelope = <T>(v: unknown, valid: (v: unknown) => v is T): T => {
  if (!exact(v, ['data']) || !valid(v.data)) throw invalid();
  return v.data;
};
function unit(v: unknown): v is TypeSalesUnitScope {
  return (
    exact(v, [
      'unit_id',
      'unit_version',
      'classification',
      'content_revision',
      'content_fingerprint',
    ]) &&
    isUuid(v.unit_id) &&
    positive(v.unit_version) &&
    (v.classification === null ||
      [
        'GENERAL',
        'MIDTERM',
        'STAY',
        'STAFF',
        'SHOWROOM',
        'SHORT_TERM',
        'FEMALE_ONLY',
        'MALE_ONLY',
      ].some((classification) => classification === v.classification)) &&
    nonnegative(v.content_revision) &&
    (v.content_fingerprint === null || typeof v.content_fingerprint === 'string')
  );
}
function sale(v: unknown): v is TypeSalesCurrentSale {
  return (
    exact(v, [
      'revision',
      'product',
      'channel',
      'first_move_in_date',
      'pending_outcome',
      'midterm_reentry_blocked',
    ]) &&
    positive(v.revision) &&
    product(v.product) &&
    (v.channel === 'own_website' || v.channel === 'external_platform') &&
    (v.first_move_in_date === null || date(v.first_move_in_date)) &&
    (v.pending_outcome === null ||
      v.pending_outcome === 'pending' ||
      v.pending_outcome === 'unknown') &&
    typeof v.midterm_reentry_blocked === 'boolean'
  );
}
function room(v: unknown): v is TypeSalesRoomScope {
  return (
    exact(v, ['room_id', 'room_version', 'topology_revision', 'sale', 'units']) &&
    isUuid(v.room_id) &&
    positive(v.room_version) &&
    positive(v.topology_revision) &&
    (v.sale === null || sale(v.sale)) &&
    Array.isArray(v.units) &&
    v.units.every(unit)
  );
}
export function isTypeSalesScope(v: unknown): v is TypeSalesScope {
  return (
    exact(v, ['property_id', 'room_type_id', 'property_version', 'type_version', 'rooms']) &&
    isUuid(v.property_id) &&
    isUuid(v.room_type_id) &&
    positive(v.property_version) &&
    positive(v.type_version) &&
    Array.isArray(v.rooms) &&
    v.rooms.every(room)
  );
}
function context(v: unknown): v is TypeSalesContext {
  return (
    exact(v, [
      'scope',
      'expected_scope_fingerprint',
      'target_product',
      'first_move_in_date',
      'allowed',
      'blocked_targets',
      'proposed_changes',
      'existing_bookings_unchanged',
    ]) &&
    isTypeSalesScope(v.scope) &&
    fingerprint(v.expected_scope_fingerprint) &&
    product(v.target_product) &&
    (v.first_move_in_date === null || date(v.first_move_in_date)) &&
    typeof v.allowed === 'boolean' &&
    Array.isArray(v.blocked_targets) &&
    v.blocked_targets.every(
      (b) =>
        exact(b, ['room_id', 'reason_codes']) &&
        (b.room_id === null || isUuid(b.room_id)) &&
        Array.isArray(b.reason_codes) &&
        b.reason_codes.every((x) => typeof x === 'string'),
    ) &&
    Array.isArray(v.proposed_changes) &&
    v.proposed_changes.every(
      (c) =>
        exact(c, ['room_id', 'changed_unit_ids', 'sale_effect']) &&
        isUuid(c.room_id) &&
        Array.isArray(c.changed_unit_ids) &&
        c.changed_unit_ids.every(isUuid) &&
        (c.sale_effect === 'INITIAL_SETUP' || c.sale_effect === 'SALE_UPDATE'),
    ) &&
    v.existing_bookings_unchanged === true
  );
}
function result(v: unknown): v is TypeSalesResult {
  return (
    exact(v, [
      'target_kind',
      'target_id',
      'result_revision',
      'before_scope',
      'after_scope',
      'room_receipts',
      'http_status',
    ]) &&
    v.target_kind === 'ROOM_TYPE' &&
    isUuid(v.target_id) &&
    positive(v.result_revision) &&
    isTypeSalesScope(v.before_scope) &&
    isTypeSalesScope(v.after_scope) &&
    Array.isArray(v.room_receipts) &&
    v.room_receipts.every(
      (r) =>
        exact(r, ['room_id', 'command_id', 'result_sale_revision']) &&
        isUuid(r.room_id) &&
        isUuid(r.command_id) &&
        positive(r.result_sale_revision),
    ) &&
    v.http_status === 200
  );
}
function validRequest(v: TypeSalesRequest, typeId: string) {
  return (
    isUuid(v.command_id) &&
    product(v.target_product) &&
    ((v.target_product === 'midterm' && date(v.first_move_in_date) && !v.reverse_confirmed) ||
      (v.target_product === 'long_term' && v.first_move_in_date === null && v.reverse_confirmed)) &&
    typeof v.reason === 'string' &&
    v.reason.trim().length > 0 &&
    v.reason.length <= 500 &&
    isTypeSalesScope(v.expected_scope) &&
    v.expected_scope.room_type_id.toLowerCase() === typeId.toLowerCase() &&
    fingerprint(v.expected_scope_fingerprint)
  );
}
function boundResult(v: unknown, typeId: string, commandId?: string): TypeSalesResult {
  const data = envelope(v, result);
  if (
    data.target_id.toLowerCase() !== typeId.toLowerCase() ||
    data.before_scope.room_type_id.toLowerCase() !== typeId.toLowerCase() ||
    data.after_scope.room_type_id.toLowerCase() !== typeId.toLowerCase() ||
    data.result_revision !== data.before_scope.type_version ||
    data.result_revision !== data.after_scope.type_version ||
    (commandId && data.room_receipts.some((r) => !isUuid(r.command_id)))
  )
    throw invalid();
  return data;
}
export async function readTypeSalesContext(
  client: ServiceClient,
  typeId: string,
  target: TypeSalesProduct,
  firstMoveInDate?: string,
): Promise<TypeSalesContext> {
  if (
    !isUuid(typeId) ||
    !product(target) ||
    (target === 'midterm' && !date(firstMoveInDate)) ||
    (target === 'long_term' && firstMoveInDate !== undefined)
  )
    throw invalid();
  const value = await client.get<unknown>(`admin/room-types/${typeId}/sales-transition-context`, {
    params: {
      target_product: target,
      ...(firstMoveInDate ? { first_move_in_date: firstMoveInDate } : {}),
    },
    preserveEnvelope: true,
  });
  const data = envelope(value, context);
  if (
    data.scope.room_type_id.toLowerCase() !== typeId.toLowerCase() ||
    data.target_product !== target ||
    data.first_move_in_date !== (firstMoveInDate ?? null)
  )
    throw invalid();
  return data;
}
export async function applyTypeSales(
  client: ServiceClient,
  typeId: string,
  body: TypeSalesRequest,
): Promise<TypeSalesResult> {
  if (!isUuid(typeId) || !validRequest(body, typeId)) throw invalid();
  const value = await client.post<unknown>(`admin/room-types/${typeId}/sales-transitions`, {
    json: body,
    preserveEnvelope: true,
  });
  return boundResult(value, typeId);
}
export async function readTypeSalesResult(
  client: ServiceClient,
  typeId: string,
  commandId: string,
): Promise<TypeSalesResult> {
  if (!isUuid(typeId) || !isUuid(commandId)) throw invalid();
  const value = await client.get<unknown>(
    `admin/room-types/${typeId}/sales-transitions/${commandId}`,
    { preserveEnvelope: true },
  );
  return boundResult(value, typeId, commandId);
}
